
const FIELD_YARDS_W = 53.3;
const FIELD_YARDS_H = 100.0;
const unitX_to_yd = FIELD_YARDS_W / 100.0;
const unitY_to_yd = FIELD_YARDS_H / 100.0;

export const clamp01 = (v) => Math.max(0, Math.min(1, v));
export const distPct = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export const ydDistAbs = (a, b) => {
    const dx = (b.x - a.x) * unitX_to_yd;
    const dy = (b.y - a.y) * unitY_to_yd;
    return Math.hypot(dx, dy);
};

export const distToSegment = (p, a, b) => {
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    if (dx === 0 && dy === 0) return distPct(p, a);
    const t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy);
    const ct = Math.max(0, Math.min(1, t));
    return distPct(p, { x: a.x + dx * ct, y: a.y + dy * ct });
};

export const absPathLengthYds = (pts) => {
    if (!pts || pts.length < 2) return 0;
    let s = 0;
    for (let i = 1; i < pts.length; i++) s += ydDistAbs(pts[i - 1], pts[i]);
    return s;
};

export const absPositionAtArclen = (pts, u) => {
    if (!pts || pts.length === 0) return { x: 0, y: 0 };
    if (pts.length === 1) return pts[0];

    const segLens = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
        const L = ydDistAbs(pts[i - 1], pts[i]);
        segLens.push(L);
        total += L;
    }
    if (total <= 0) return pts[0];

    const target = clamp01(u) * total;
    let acc = 0;
    for (let i = 0; i < segLens.length; i++) {
        const L = segLens[i];
        if (acc + L >= target) {
            const a = pts[i], b = pts[i + 1];
            const t = L === 0 ? 0 : (target - acc) / L;
            return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
        }
        acc += L;
    }
    return pts[pts.length - 1];
};

export const pointsToPath = (points) => {
    if (!points || points.length === 0) return '';
    let d = `M ${points[0].x} ${points[0].y}`;
    for (let i = 1; i < points.length; i++) d += ` L ${points[i].x} ${points[i].y}`;
    return d;
};

export const shiftPolyline = (pts, dx, dy) => pts.map(p => ({ x: p.x + dx, y: p.y + dy }));

export const uAlongPolylineClosest = (pts, clickPos) => {
    if (!pts || pts.length < 2) return 0;
    let segLens = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
        const L = ydDistAbs(pts[i - 1], pts[i]);
        segLens.push(L);
        total += L;
    }
    if (total <= 0) return 0;

    let bestU = 0;
    let bestDist = Infinity;
    let acc = 0;

    for (let i = 1; i < pts.length; i++) {
        const segLen = segLens[i - 1];
        if (segLen <= 0) continue;

        const a = pts[i - 1];
        const b = pts[i];

        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const den = dx * dx + dy * dy;
        const t = den === 0 ? 0 : ((clickPos.x - a.x) * dx + (clickPos.y - a.y) * dy) / den;
        const clampedT = Math.max(0, Math.min(1, t));
        const proj = { x: a.x + dx * clampedT, y: a.y + dy * clampedT };

        const d = distPct(proj, clickPos);
        if (d < bestDist) {
            bestDist = d;
            bestU = (acc + clampedT * segLen) / total;
        }
        acc += segLen;
    }
    return bestU;
};

export const anchoredPolylineFor = (route, owner, originalPositions = {}, isPlaying = false) => {
    if (!route || !owner || !route.pointsAbs || route.pointsAbs.length < 2) return null;

    const anchorPos = isPlaying
        ? (originalPositions[owner.id] || { x: owner.x, y: owner.y })
        : { x: owner.x, y: owner.y };

    const dx = anchorPos.x - (route.drawBase?.x ?? route.pointsAbs[0].x);
    const dy = anchorPos.y - (route.drawBase?.y ?? route.pointsAbs[0].y);
    return shiftPolyline(route.pointsAbs, dx, dy);
};

export const computeMotionSnapTime = (players, routes) => {
    const motionRoute = routes.find(r => r.motionU !== null && r.motionU !== undefined);
    if (!motionRoute) return null;

    const owner = players.find(p => p.id === motionRoute.playerId);
    if (!owner) return null;

    const poly = anchoredPolylineFor(motionRoute, owner);
    const Lyds = absPathLengthYds(poly);
    const forty = owner.speed || 5.0;
    const yps = forty > 0 ? 40 / forty : 8;
    const totalT = Lyds / yps;

    // The snap occurs exactly when the motion player reaches their motionU point.
    return (motionRoute.motionU * totalT) + (owner.routeDelay || 0);
};

export const getPlayerPosAtTime = (player, t, routes, originalPositions = {}, isPlaying = false, ballSpec = null, allPlayers = []) => {
    if (player.visualOnly) return { x: player.x, y: player.y };

    const route = routes.find(r => r.playerId === player.id && r.mode === 'absolute-anchored');
    if (!route) return { x: player.x, y: player.y };

    let delay = player.routeDelay || 0;

    // SYNC LOGIC: If there is a motion player in the play, everyone else waits for that snap
    if (ballSpec && allPlayers.length > 0) {
        const motionSnapTime = computeMotionSnapTime(allPlayers, routes);
        if (motionSnapTime !== null) {
            // Only offensive players (except the motion man themselves) wait for the snap
            // We check motionU on the route to see if THIS player is the motion player
            if (player.type === 'offense' && (route.motionU === undefined || route.motionU === null)) {
                delay = Math.max(delay, motionSnapTime);
            }
        }
    }

    const activeTime = t - delay;
    if (activeTime <= 0) return { x: player.x, y: player.y };

    const poly = anchoredPolylineFor(route, player, originalPositions, isPlaying);
    if (!poly || poly.length < 2) return { x: player.x, y: player.y };

    const Lyds = absPathLengthYds(poly);
    const forty = player.speed || 6.0;
    const yps = forty > 0 ? 40 / forty : 8;
    const totalT = Lyds / yps;

    const u = totalT <= 0 ? 0 : clamp01(activeTime / totalT);
    return absPositionAtArclen(poly, u);
};

export const computeHandoffTime = (p1, p2, minTime, routes, playDuration = 10, originalPositions = {}, isPlaying = false, ballSpec, allPlayers) => {
    const delay = p1.handoffDelay || 0;
    const scanStart = minTime + delay;
    const HANDOFF_RADIUS_YDS = 8.0;

    for (let t = scanStart; t <= playDuration; t += 0.05) {
        const pos1 = getPlayerPosAtTime(p1, t, routes, originalPositions, isPlaying, ballSpec, allPlayers);
        const pos2 = getPlayerPosAtTime(p2, t, routes, originalPositions, isPlaying, ballSpec, allPlayers);
        const d = ydDistAbs(pos1, pos2);
        if (d <= HANDOFF_RADIUS_YDS) return t;
    }
    return null;
};

export const THROW_SPEEDS_YPS = {
    pass: {
        pro: 29, college: 25, hs: 22, peewee: 14,
        flag_adult: 24, flag_13plus: 20, flag_12u: 15, flag_10u: 13
    },
    toss: {
        pro: 14, college: 12, hs: 11, peewee: 7,
        flag_adult: 12, flag_13plus: 10, flag_12u: 8, flag_10u: 7
    }
};

export const getDynamicBallSegments = (ballSpec, players, routes, profileConfig, originalPositions = {}, isPlaying = false) => {
    if (!ballSpec || !ballSpec.enabled || !ballSpec.segments) return [];

    // Dynamic snap time from motion (or 0 if none)
    const motionSnapTime = computeMotionSnapTime(players, routes);
    const effectiveSnapTime = motionSnapTime !== null ? motionSnapTime : 0;

    const result = [];
    let minStartTime = 0;

    for (const raw of ballSpec.segments) {
        if (raw.kind === 'snap') {
            const snapDuration = ballSpec.snapTime || 0.4;
            result.push({
                ...raw,
                startTime: effectiveSnapTime,
                endTime: effectiveSnapTime + snapDuration,
                fromPos: ballSpec.startPos
            });
            minStartTime = effectiveSnapTime + snapDuration;
        } else if (raw.kind === 'handoff') {
            const holder = players.find(p => p.id === raw.fromPlayerId);
            const receiver = players.find(p => p.id === raw.toPlayerId);
            const t = (holder && receiver) ? computeHandoffTime(holder, receiver, minStartTime, routes, 10, originalPositions, isPlaying, ballSpec, players) : null;
            if (t !== null) {
                result.push({ ...raw, startTime: t, endTime: t + 0.5 });
                minStartTime = t + 0.5;
            } else {
                result.push({ ...raw, startTime: minStartTime, endTime: minStartTime + 0.5 });
                minStartTime += 0.5;
            }
        } else if (raw.kind === 'throw') {
            const holder = players.find(p => p.id === raw.fromPlayerId);
            const receiver = players.find(p => p.id === raw.toPlayerId);
            const route = routes.find(r => r.id === raw.routeId);
            if (holder && receiver && route) {
                const poly = anchoredPolylineFor(route, receiver, originalPositions, isPlaying);
                const Lyds = absPathLengthYds(poly);
                const forty = receiver.speed || 4.8;
                const yps = forty > 0 ? 40 / forty : 8;
                const totalT = Lyds / yps;

                // Recalculate effective delay for this receiver
                let rDelay = parseFloat(receiver.routeDelay) || 0;
                if (motionSnapTime !== null && receiver.type === 'offense' && (route.motionU === undefined || route.motionU === null)) {
                    rDelay = Math.max(rDelay, motionSnapTime);
                }

                const catchTime = rDelay + (raw.targetU * totalT);
                const tDelay = parseFloat(holder.throwDelay) || 0;

                const ttype = ballSpec.throwType || 'pass';
                const profileThrowKey = profileConfig?.throwProfileKey || 'flag_12u';
                const finalThrowKey = THROW_SPEEDS_YPS[ttype][profileThrowKey] ? profileThrowKey : 'flag_12u';
                const ballYps = THROW_SPEEDS_YPS[ttype][finalThrowKey] || 15;

                // [FIX] Calculate release time based on ACTUAL flight distance/speed
                // Previously was clamped to (catchTime - 0.5), forcing long throws to arrive late.

                // 1. Where is the receiver supposed to be?
                const intendedCatchPos = getPlayerPosAtTime(receiver, catchTime, routes, originalPositions, isPlaying, ballSpec, players);

                // 2. Where is the holder (QB) at the earliest possible release time?
                const earliestRelease = minStartTime + tDelay;
                const estReleasePos = getPlayerPosAtTime(holder, earliestRelease, routes, originalPositions, isPlaying, ballSpec, players);

                // 3. How long does the ball take to get there?
                const estDist = ydDistAbs(estReleasePos, intendedCatchPos);
                const estFlightT = estDist / ballYps;

                // 4. Back-calculate the required release time to hit the target ON TIME
                // If idealReleaseTime < earliestRelease, we have to throw late.
                const releaseTime = Math.max(earliestRelease, catchTime - estFlightT);

                // 5. Finalize Physics
                const finalReleasePos = getPlayerPosAtTime(holder, releaseTime, routes, originalPositions, isPlaying, ballSpec, players);

                // If we are late, the receiver might have moved, but usually we throw to the spot or the receiver updates.
                // Let's recalculate the exact impact time based on the finalized release position and target spot logic.
                // Note: The original logic looked up receiver pos at CatchTime. 
                // We should probably check where the receiver IS at the NEW catch time if we are late?
                // For now, let's keep it simple: Throw to the intended spot if on time, or chase them if late?
                // Original behavior was: throw to `getPlayerPosAtTime(receiver, finalCatchTime)` which means "lead the receiver"

                // Let's iterate once to converge if we are fast-moving
                let finalCatchTime = releaseTime + estFlightT; // Initial guess
                let finalCatchPos = getPlayerPosAtTime(receiver, finalCatchTime, routes, originalPositions, isPlaying, ballSpec, players);
                const finalDist = ydDistAbs(finalReleasePos, finalCatchPos);
                finalCatchTime = releaseTime + (finalDist / ballYps);

                result.push({
                    ...raw,
                    startTime: releaseTime,
                    endTime: finalCatchTime,
                    releaseTime: releaseTime,
                    catchTime: finalCatchTime,
                    fromPos: finalReleasePos,
                    toPos: getPlayerPosAtTime(receiver, finalCatchTime, routes, originalPositions, isPlaying, ballSpec, players)
                });
                minStartTime = finalCatchTime;
            }
        } else if (raw.kind === 'postCatchCarry') {
            result.push({ ...raw, startTime: minStartTime });
        }
    }
    return result;
};

export const findUOnPolyline = (pts, clickPt) => {
    if (!pts || pts.length < 2) return 0;
    const totalDist = absPathLengthYds(pts);
    if (totalDist <= 0) return 0;

    let bestDist = Infinity;
    let distAtBest = 0;
    let acc = 0;

    for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i];
        const b = pts[i + 1];
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const L2 = dx * dx + dy * dy;
        if (L2 === 0) continue;

        let t = ((clickPt.x - a.x) * dx + (clickPt.y - a.y) * dy) / L2;
        t = Math.max(0, Math.min(1, t));

        const proj = { x: a.x + t * dx, y: a.y + t * dy };
        const dClick = distPct(clickPt, proj);

        if (dClick < bestDist) {
            bestDist = dClick;
            const segDist = ydDistAbs(a, b);
            distAtBest = acc + (t * segDist);
        }
        acc += ydDistAbs(a, b);
    }
    return distAtBest / totalDist;
};

export const splitPolylineAtU = (pts, u) => {
    if (!pts || pts.length < 2) return { pre: [], post: pts };
    if (u <= 0) return { pre: [], post: pts };
    if (u >= 1) return { pre: pts, post: [] };

    const segLens = [];
    let total = 0;
    for (let i = 1; i < pts.length; i++) {
        const L = ydDistAbs(pts[i - 1], pts[i]);
        segLens.push(L);
        total += L;
    }

    if (total <= 0) return { pre: pts, post: [] };

    const target = u * total;
    let acc = 0;

    const pre = [pts[0]];
    const post = [];

    let splitFound = false;

    for (let i = 0; i < segLens.length; i++) {
        const L = segLens[i];
        if (!splitFound) {
            if (acc + L >= target) {
                // Split occurs in this segment
                const a = pts[i];
                const b = pts[i + 1];
                const t = L === 0 ? 0 : (target - acc) / L;
                const splitPt = { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };

                pre.push(splitPt);
                post.push(splitPt);
                splitFound = true;

                // Add the end of this segment to post as well
                post.push(pts[i + 1]);
            } else {
                pre.push(pts[i + 1]);
            }
        } else {
            post.push(pts[i + 1]);
        }
        acc += L;
    }

    return { pre, post };
};
